<script setup lang="ts">
import { computed } from 'vue'
import { useDisplay } from 'vuetify'

const { mobile } = useDisplay()
const isMobile = computed(() => mobile.value)

const props = withDefaults(
  defineProps<{
    modelValue: boolean
    open?: boolean
    id?: string
    title?: string
    text?: string
    loading?: boolean
    maxWidth?: string
    rounded?: string
    close?: boolean
    titlePaddingBottom?: number
    contentClass?: string
    actionsClass?: string
    escCloses?: boolean
  }>(),
  {
    modelValue: false,
    open: undefined,
    loading: false,
    maxWidth: '960px',
    rounded: undefined,
    id: undefined,
    title: undefined,
    text: undefined,
    close: false,
    titlePaddingBottom: undefined,
    contentClass: '',
    actionsClass: '',
    escCloses: false,
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', v: boolean): void
  (e: 'update:open', v: boolean): void
  (e: 'close'): void
}>()

const proxyOpen = computed<boolean>({
  get: () => props.open ?? props.modelValue,
  set: (v) => {
    emit('update:modelValue', v)
    emit('update:open', v)
    if (!v) emit('close')
  },
})

const resolvedTitlePaddingBottom = computed(() => {
  if (props.titlePaddingBottom !== undefined) {
    return `${props.titlePaddingBottom}px`
  }

  return isMobile.value ? '16px' : '24px'
})

const cardRadius = computed(
  () => props.rounded ?? (isMobile.value ? '12px' : '4px')
)

const titleWrapperStyle = computed(() => ({
  paddingTop: isMobile.value ? '16px' : '24px',
  paddingRight: isMobile.value ? '16px' : '24px',
  paddingLeft: isMobile.value ? '16px' : '24px',
  paddingBottom: resolvedTitlePaddingBottom.value,
}))

const textPaddingClass = computed(() =>
  isMobile.value ? 'px-4 pt-0 pb-0' : 'px-6 pt-0 pb-0'
)

const actionsPaddingClass = computed(() =>
  isMobile.value ? 'pa-4 pt-0' : 'px-6 pt-0 pb-6'
)
</script>

<template>
  <v-dialog
    :id="id"
    v-model="proxyOpen"
    :persistent="props.escCloses === true ? false : true"
    :max-width="isMobile ? 'calc(100vw - 32px)' : maxWidth"
    :width="isMobile ? 'calc(100vw - 32px)' : undefined"
    scrollable
  >
    <v-card
      class="dialog-large-card text-start"
      color="white"
      elevation="24"
      :style="{
        maxHeight: isMobile ? 'calc(100vh - 32px)' : '90vh',
        borderRadius: cardRadius,
        width: '100%',
      }"
    >
      <v-card-title
        v-if="title || $slots.title || props.close"
        class="dialog-large-title-wrapper"
        :style="titleWrapperStyle"
      >
        <p class="dialog-large-title ma-0">
          <span v-if="title">{{ title }}</span>
          <slot name="title" />
        </p>

        <v-btn
          v-if="props.close"
          icon
          variant="plain"
          density="compact"
          size="small"
          :ripple="false"
          class="dialog-large-close"
          @click="proxyOpen = false"
        >
          <v-icon size="20">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text
        class="dialog-large-text"
        :class="[textPaddingClass, props.contentClass]"
      >
        <slot />
        <p v-if="text" class="text-body-1 ma-0">{{ text }}</p>
      </v-card-text>

      <v-card-actions v-if="$slots.actions" class="pa-0">
        <div
          :class="[
            actionsPaddingClass,
            props.actionsClass,
            'dialog-large-actions',
          ]"
        >
          <slot v-if="!loading" name="actions" />
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.dialog-large-card {
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.dialog-large-title-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  min-width: 0;
  flex: 0 0 auto;
}

.dialog-large-title {
  color: var(--text-text--primary, var(--black-0-87, rgba(0, 0, 0, 0.87)));
  font-family: var(--Typography-fontFamilies-Open-Sans, 'Open Sans');
  font-size: var(--fontSize-35, 20px);
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.05px;
  flex: 1 1 auto;
  min-width: 0;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.dialog-large-close {
  flex: 0 0 auto;
}

.dialog-large-text {
  color: rgba(0, 0, 0, 0.6);
  font-family: 'Open Sans';
  font-size: var(--fontSize-49, 14px);
  font-style: normal;
  font-weight: 400;
  line-height: var(--lineHeights-49, 22px);
  letter-spacing: 0.014px;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

:deep(.v-card-actions) {
  min-height: 0 !important;
  padding: 0 !important;
  align-items: stretch !important;
  flex: 0 0 auto;
}

.dialog-large-actions {
  width: 100%;
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  align-items: center;
  background: #fff;
}

.dialog-large-actions :deep(.v-btn) {
  margin: 0 !important;
}

@media (max-width: 959px) {
  .dialog-large-title {
    color: var(--text-text--primary, var(--black-0-87, rgba(0, 0, 0, 0.87)));
    font-feature-settings: 'liga' off;
    font-family: var(--Typography-fontFamilies-Open-Sans, 'Open Sans');
    font-size: var(--fontSize-42, 16px);
    font-style: normal;
    font-weight: 600;
    line-height: 20px;
    letter-spacing: 0.08px;
  }

  .dialog-large-actions {
    display: flex;
    flex-direction: column;
    gap: 16px;
    justify-content: stretch;
    align-items: stretch;
  }

  .dialog-large-actions :deep(.v-btn) {
    width: 100%;
    flex: 0 0 auto;
    min-width: 0;
  }
}

:deep(.v-overlay__content) {
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
}
</style>
